import { memo, useState, useCallback } from 'react'
import styled from 'styled-components'
import { orval_CS } from 'back-end-api'
import { useTranslation } from 'services/Translation'
import { primary, textPrimary, greySecondary } from 'styles/Colors'

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`

const Modal = styled.div`
  background: #fff;
  border-radius: 12px;
  width: 440px;
  max-width: 92vw;
  padding: 28px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`

const Title = styled.h2`
  font-family: coresanscr65, sans-serif;
  font-size: 1.125rem;
  color: ${textPrimary};
  margin: 0 0 20px;
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 14px;
`

const Label = styled.label`
  font-family: coresanscr55, sans-serif;
  font-size: 0.8125rem;
  color: ${textPrimary};
`

const Input = styled.input<{ hasError?: boolean }>`
  padding: 9px 12px;
  border: 1px solid ${({ hasError }) => (hasError ? '#DC2626' : '#D1D5DB')};
  border-radius: 6px;
  font-family: coresanscr55, sans-serif;
  font-size: 0.875rem;
  outline: none;
  &:focus { border-color: ${({ hasError }) => (hasError ? '#DC2626' : primary)}; }
`

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-family: coresanscr55, sans-serif;
  font-size: 0.875rem;
  outline: none;
  background: #fff;
  &:focus { border-color: ${primary}; }
`

const Row = styled.div`
  display: flex;
  gap: 12px;
`

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
`

const Btn = styled.button<{ isPrimary?: boolean }>`
  padding: 10px 22px;
  border-radius: 6px;
  font-family: coresanscr65, sans-serif;
  font-size: 0.8125rem;
  cursor: pointer;
  border: 1px solid ${({ isPrimary }) => (isPrimary ? primary : greySecondary)};
  background: ${({ isPrimary }) => (isPrimary ? primary : '#fff')};
  color: ${({ isPrimary }) => (isPrimary ? '#fff' : textPrimary)};
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const ErrorText = styled.div`
  color: #DC2626;
  font-size: 0.8125rem;
  font-family: coresanscr55, sans-serif;
  margin-top: 4px;
`

const SuccessText = styled.div`
  color: #10B981;
  font-size: 0.875rem;
  font-family: coresanscr65, sans-serif;
  text-align: center;
  padding: 20px 0;
`

const texts = (getText: (text: string, key: string) => string) => (key: string) =>
  ({
    title: getText('Patiënt uitnodigen voor MIA', 'TopBar_Invite'),
    firstName: getText('Voornaam', 'TopBar_Invite'),
    lastName: getText('Achternaam', 'TopBar_Invite'),
    email: getText('E-mail', 'TopBar_Invite'),
    phone: getText('Telefoonnummer', 'TopBar_Invite'),
    language: getText('Taal', 'TopBar_Invite'),
    send: getText('Uitnodiging versturen', 'TopBar_Invite'),
    cancel: getText('Annuleren', 'TopBar_Invite'),
    success: getText('Uitnodiging succesvol verstuurd!', 'TopBar_Invite'),
    errorRequired: getText('Voornaam en achternaam zijn verplicht', 'TopBar_Invite'),
    errorContact: getText('E-mail of telefoonnummer is verplicht', 'TopBar_Invite'),
    errorPhoneFormat: getText('Telefoonnummer moet beginnen met een landcode (bijv. +31)', 'TopBar_Invite'),
    sendAnother: getText('Nog een uitnodiging versturen', 'TopBar_Invite'),
  }[key])

interface InviteModalProps {
  onClose: () => void
}

const InviteModal = ({ onClose }: InviteModalProps) => {
  const { t } = useTranslation(texts)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [lang, setLang] = useState('nl')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSend = useCallback(async () => {
    setError(null)
    setPhoneError(null)
    if (!firstName.trim() || !lastName.trim()) {
      setError(t('errorRequired'))
      return
    }
    if (!email.trim() && !phone.trim()) {
      setError(t('errorContact'))
      return
    }
    if (phone.trim() && !/^\+\d{2,}/.test(phone.trim())) {
      const msg = t('errorPhoneFormat')
      setError(msg)
      setPhoneError(msg)
      return
    }

    setLoading(true)
    try {
      const body: any = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        recipient_language_tag: lang,
      }
      if (email.trim()) body.email = email.trim()
      if (phone.trim()) body.mobile_phone = phone.trim()

      const res = await orval_CS.invitePatient(body, { useToken: true })
      if (res.error) {
        setError(res.error.message || 'Something went wrong')
      } else {
        setSuccess(true)
      }
    } catch (err: any) {
      setError(err?.message || 'Something went wrong')
    }
    setLoading(false)
  }, [firstName, lastName, email, phone, lang, t])

  const handleReset = useCallback(() => {
    setFirstName('')
    setLastName('')
    setEmail('')
    setPhone('')
    setError(null)
    setPhoneError(null)
    setSuccess(false)
  }, [])

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Title>{t('title')}</Title>
        {success ? (
          <>
            <SuccessText>{t('success')}</SuccessText>
            <Buttons>
              <Btn onClick={handleReset}>{t('sendAnother')}</Btn>
              <Btn isPrimary onClick={onClose}>OK</Btn>
            </Buttons>
          </>
        ) : (
          <>
            <Row>
              <FieldGroup style={{ flex: 1 }}>
                <Label>{t('firstName')} *</Label>
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </FieldGroup>
              <FieldGroup style={{ flex: 1 }}>
                <Label>{t('lastName')} *</Label>
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </FieldGroup>
            </Row>
            <FieldGroup>
              <Label>{t('email')}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </FieldGroup>
            <FieldGroup>
              <Label>{t('phone')}</Label>
              <Input
                type="tel"
                value={phone}
                hasError={!!phoneError}
                onChange={(e) => { setPhone(e.target.value); setPhoneError(null) }}
                onBlur={() => {
                  if (phone.trim() && !/^\+\d{2,}/.test(phone.trim())) {
                    setPhoneError(t('errorPhoneFormat'))
                  }
                }}
                placeholder="+31..."
              />
              {phoneError && <ErrorText>{phoneError}</ErrorText>}
            </FieldGroup>
            <FieldGroup>
              <Label>{t('language')}</Label>
              <Select value={lang} onChange={(e) => setLang(e.target.value)}>
                <option value="nl">Nederlands</option>
                <option value="en">English</option>
                <option value="pl">Pools</option>
                {/* <option value="ar">العربية</option> */}
              </Select>
            </FieldGroup>
            {error && <ErrorText>{error}</ErrorText>}
            <Buttons>
              <Btn onClick={onClose} disabled={loading}>{t('cancel')}</Btn>
              <Btn isPrimary onClick={handleSend} disabled={loading}>
                {loading ? '...' : t('send')}
              </Btn>
            </Buttons>
          </>
        )}
      </Modal>
    </Overlay>
  )
}

export default memo(InviteModal)
